// La MARCA que el diseñador pinta con el Marcador sobre una imagen de la mezcla
// (ADR-2026-09-21-el-marcador-de-la-mezcla): trazos de pincel semitransparente en
// coordenadas RELATIVAS a la imagen (0..1 desde arriba a la izquierda), así valen igual en
// la miniatura del lienzo, en el original que mira la visión y con cualquier zoom. Sobre
// una PARTE dice "de esta imagen, tomá lo que pinté"; sobre la BASE, "el cambio va acá".
// Sin React ni DOM: lo usan el adorno, la persistencia y el director.

export interface PuntoDeMarca {
  x: number;
  y: number;
}

export interface Marca {
  /** Cada trazo, una pasada del pincel: sus puntos en orden. */
  trazos: PuntoDeMarca[][];
  /** El ancho del pincel como fracción del lado menor de la imagen. */
  grosor: number;
}

/** El pincel: un marcador ancho, para señalar una zona y no para dibujar fino. */
export const GROSOR_DE_MARCA = 0.05;
/** Un trazo más corto que esto no cuenta como marca. */
export const LARGO_MINIMO = 0.01;
/** Dos puntos más cerca que esto se funden: menos peso guardado sin perder forma. */
const PASO_MINIMO = 0.004;
const TOPE_DE_PUNTOS = 4000;

const acotar = (v: number): number => Math.min(1, Math.max(0, v));
/** 4 decimales: de sobra para una imagen y sin colas de coma flotante en lo guardado. */
const redondear = (v: number): number => Math.round(v * 10_000) / 10_000;
const punto = (p: PuntoDeMarca): PuntoDeMarca => ({ x: redondear(acotar(p.x)), y: redondear(acotar(p.y)) });
const distancia = (a: PuntoDeMarca, b: PuntoDeMarca): number => Math.hypot(a.x - b.x, a.y - b.y);

export const marcaVacia = (): Marca => ({ trazos: [], grosor: GROSOR_DE_MARCA });

/** Suma un punto al trazo si se movió lo suficiente desde el último. */
export function conPunto(trazo: PuntoDeMarca[], p: PuntoDeMarca): PuntoDeMarca[] {
  const q = punto(p);
  const ultimo = trazo[trazo.length - 1];
  if (ultimo && distancia(ultimo, q) < PASO_MINIMO) return trazo;
  return [...trazo, q];
}

export const largoDeTrazo = (trazo: PuntoDeMarca[]): number =>
  trazo.reduce((acc, p, i) => (i === 0 ? 0 : acc + distancia(trazo[i - 1], p)), 0);

/** Un trazo cuenta si se movió; un click sin arrastre no marca nada. */
export const esTrazoValido = (trazo: PuntoDeMarca[]): boolean => trazo.length >= 2 && largoDeTrazo(trazo) >= LARGO_MINIMO;

/** La marca con un trazo más (si vale), sin mutar. */
export function conTrazo(marca: Marca | undefined, trazo: PuntoDeMarca[]): Marca | undefined {
  if (!esTrazoValido(trazo)) return marca;
  const base = marca ?? marcaVacia();
  return { ...base, trazos: [...base.trazos, trazo] };
}

export const esMarcaValida = (m: Marca): boolean => m.trazos.some(esTrazoValido);

/** Lo guardado (o lo que llega de afuera) como marca, o `undefined` si no es una. */
export function normalizarMarca(raw: unknown): Marca | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as Record<string, unknown>;
  if (!Array.isArray(r.trazos)) return undefined;
  let puntos = 0;
  const trazos: PuntoDeMarca[][] = [];
  for (const t of r.trazos) {
    if (!Array.isArray(t)) continue;
    const trazo: PuntoDeMarca[] = [];
    for (const p of t) {
      if (!p || typeof p !== 'object') continue;
      const { x, y } = p as { x?: unknown; y?: unknown };
      if (typeof x !== 'number' || typeof y !== 'number' || !Number.isFinite(x) || !Number.isFinite(y)) continue;
      trazo.push(punto({ x, y }));
      if (++puntos > TOPE_DE_PUNTOS) return undefined;
    }
    if (esTrazoValido(trazo)) trazos.push(trazo);
  }
  if (trazos.length === 0) return undefined;
  const grosor = typeof r.grosor === 'number' && r.grosor > 0 && r.grosor <= 0.5 ? redondear(r.grosor) : GROSOR_DE_MARCA;
  return { trazos, grosor };
}

/** El rectángulo que envuelve todo lo pintado, con el ancho del pincel incluido. */
export function cajaDeMarca(m: Marca): { x1: number; y1: number; x2: number; y2: number } {
  let x1 = 1;
  let y1 = 1;
  let x2 = 0;
  let y2 = 0;
  for (const t of m.trazos) {
    for (const p of t) {
      x1 = Math.min(x1, p.x);
      y1 = Math.min(y1, p.y);
      x2 = Math.max(x2, p.x);
      y2 = Math.max(y2, p.y);
    }
  }
  const medio = m.grosor / 2;
  return { x1: redondear(acotar(x1 - medio)), y1: redondear(acotar(y1 - medio)), x2: redondear(acotar(x2 + medio)), y2: redondear(acotar(y2 + medio)) };
}

const pct = (v: number): string => `${Math.round(v * 100)}%`;

/**
 * La marca contada a la visión, que ve la pintura pero también necesita el número por
 * si la confunde con la imagen: "del 20% al 45% del ancho desde la izquierda, del 10% al
 * 30% del alto desde arriba".
 */
export function describirMarca(m: Marca): string {
  const c = cajaDeMarca(m);
  return `del ${pct(c.x1)} al ${pct(c.x2)} del ancho (desde la izquierda) y del ${pct(c.y1)} al ${pct(c.y2)} del alto (desde arriba)`;
}
